import { useState } from 'react';

import { billingApi } from '../../api/billing.js';
import { pushQueueApi } from '../../api/pushQueue.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { Permission } from '../../auth/roles.js';
import { actionsForStatuses } from '../Records/RecordsTabs.jsx';

// What this record can have done to it, right here, without going back to the
// table. The eligibility rules come from `actionsForStatuses` — the same
// function the table's action bar uses — so there is exactly one answer to
// "what can I do to a record in status X".
//
// Every action ends by re-reading the record from the API rather than assuming
// it landed. That is not politeness: this page is designed to be SHARED, so two
// people acting on the same record at once is now the expected case, not the
// edge case. The backend re-reads the live status before it does anything (see
// push_worker.void_record / repush_record and _mark_terminal), so a second
// actor gets a refusal rather than a wrong write — and the refusal has to be
// visible here, next to a status that has just changed under them.
export function RecordActions({ record, onChanged, nsConflict }) {
  const { hasPermission } = useAuth();
  const canApprove = hasPermission(Permission.APPROVE_RECORDS);
  const canPush    = hasPermission(Permission.PUSH_TO_NETSUITE);

  const [busy, setBusy] = useState(null);   // null | label
  const [msg,  setMsg]  = useState(null);   // {type, text}

  const id = record.id;
  const allowed = actionsForStatuses(
    new Set([record.status]),
    new Set([record.exclusion_reason || '']),
  );

  const show = {
    approve:      allowed.approve      && canApprove,
    push:         allowed.push         && canPush,
    void:         allowed.void         && canPush,
    retry:        allowed.retry        && canPush,
    repush:       allowed.repush       && canPush,
    markHandled:  allowed.markHandled  && canApprove,
    unhandle:     allowed.unhandle     && canApprove,
    markExcluded: allowed.markExcluded && canApprove,
    unexclude:    allowed.unexclude    && canApprove,
  };
  const anyAction = Object.values(show).some(Boolean);

  // One wrapper for every button: guard against double-clicks, run, report,
  // then re-read. `fn` returns the success text; throwing surfaces the
  // backend's own message, which is where a concurrent-action refusal
  // ("Cannot void record in status='approved'") reaches the user intact.
  // `settles` marks the two actions the backend runs on a BACKGROUND thread
  // (Void, Re-sync). Their response only confirms the work was handed off — it
  // is returned before the NetSuite calls run, and notably it says
  // "void_started: 1" even when void_record then refuses the record because
  // its status changed underneath. So for those we re-read again a few seconds
  // later and let the status badge tell the truth rather than the response.
  const run = async (label, fn, { settles = false } = {}) => {
    if (busy) return;
    setBusy(label);
    setMsg(null);
    try {
      const text = await fn();
      setMsg({ type: 'success', text });
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setBusy(null);
      if (onChanged) await onChanged();
      if (settles) setTimeout(() => { if (onChanged) onChanged(); }, 6000);
    }
  };

  const sel = () => ({ record_ids: [id] });

  const approve = () => run('Approving', async () => {
    const r = await billingApi.updateStatus(sel(), 'approved');
    if (!r.updated) {
      // Two different refusals with two different fixes, and the backend now
      // counts them separately: wrong status (someone else moved it) vs
      // nothing billable to push.
      throw new Error(r.skipped_unpushable
        ? 'Not approved — this record has no billable line items (every line is '
          + 'excluded, zero-hour, or has no rate yet).'
        : 'Not approved — this record is no longer Pending. Someone else has '
          + 'acted on it; the status above is now current.');
    }
    return 'Approved.';
  });

  const push = () => run('Syncing to NetSuite', async () => {
    const r = await pushQueueApi.pushRecords(sel());
    if (!r.queued) throw new Error('Nothing was queued — the record is no longer Approved.');
    return 'Queued for NetSuite sync — the background worker picks it up shortly.';
  });

  const voidIt = () => run('Voiding in NetSuite', async () => {
    if (!window.confirm(
      'Void this record in NetSuite?\n\nEvery NetSuite TimeBill for this punch '
      + 'is deleted and the record returns to Pending for re-review.',
    )) return 'Cancelled.';
    const r = await billingApi.voidRecords(sel());
    return r.message
      || 'Void started in NetSuite — the status below updates when it settles.';
  }, { settles: true });

  const repush = () => run('Re-syncing in NetSuite', async () => {
    if (!window.confirm(
      'Re-sync this record?\n\nThe existing NetSuite TimeBills are voided and '
      + 'replaced with the current line items.',
    )) return 'Cancelled.';
    const r = await billingApi.repushRecords(sel());
    if (r.started) return 'Re-sync started — the status below updates when it settles.';
    const failed = (r.results || []).filter((x) => !x.success);
    if (failed.length) {
      throw new Error(failed[0].error || 'Re-sync failed — check NetSuite for partials.');
    }
    if (!r.repushed) {
      throw new Error('Not re-synced — this record is no longer in content drift. '
        + 'Someone else has acted on it; the status above is now current.');
    }
    return 'Re-queued — a new TimeBill will be created in NetSuite shortly.';
  }, { settles: true });

  const retry = () => run('Retrying sync', async () => {
    const r = await billingApi.retryRecords(sel());
    if (!r.reopened) throw new Error('Nothing was re-queued — the record is no longer Failed.');
    return `Re-queued (${r.queued_lines} sync row${r.queued_lines === 1 ? '' : 's'}).`;
  });

  const markHandled = () => run('Marking handled', async () => {
    const note = window.prompt(
      'Mark this record as Manually Handled?\n\n'
      + 'It leaves the queue for good — no reprocess, drift check or Ops Review '
      + 'will move it again. Use this only when the punch has been billed '
      + 'outside the app.\n\nOptional note (why / where it was billed):',
      '',
    );
    if (note === null) return 'Cancelled.';
    const r = await billingApi.markManuallyHandled(sel(), note || null);
    if (!r.updated) {
      throw new Error('Not marked — the record is no longer in an eligible status '
        + '(only Pending and Ops Review records can be marked).');
    }
    return 'Marked manually handled.';
  });

  const unhandle = () => run('Undoing', async () => {
    if (!window.confirm(
      'Undo Manually Handled?\n\nThe record goes back to where it came from — '
      + 'Pending to Pending, Ops Review to Ops Review. The next reprocess then '
      + 're-derives its real status.',
    )) return 'Cancelled.';
    const r = await billingApi.unmarkManuallyHandled(sel());
    if (!r.updated) throw new Error('Nothing changed — the record is no longer Manually Handled.');
    return 'Un-handled.';
  });

  const markExcluded = () => run('Excluding', async () => {
    const note = window.prompt(
      'Manually exclude this record?\n\n'
      + 'It moves to Excluded and stays there — no reprocess will bring it back. '
      + "Use this when the punch should not be billed for a reason the engine "
      + "can't know.\n\nOptional note (why):",
      '',
    );
    if (note === null) return 'Cancelled.';
    const r = await billingApi.markExcluded(sel(), note || null);
    if (!r.updated) {
      throw new Error('Not excluded — the record is no longer Pending or a Gap.');
    }
    return 'Manually excluded.';
  });

  const unexclude = () => run('Undoing exclusion', async () => {
    if (!window.confirm(
      'Undo the manual exclusion?\n\nThe record goes back to Pending or Gaps, '
      + 'whichever it came from.',
    )) return 'Cancelled.';
    const r = await billingApi.unmarkExcluded(sel());
    if (!r.updated) throw new Error('Nothing changed — this is no longer a manual exclusion.');
    return 'Exclusion undone.';
  });

  // NetSuite-touching buttons stand down while a pipeline is already using the
  // NetSuite API, exactly as they do on the table.
  const nsBlocked = Boolean(nsConflict);
  const nsTitle = nsConflict
    ? `${nsConflict.label || 'NetSuite'} in progress${nsConflict.eta_minutes ? ` (~${nsConflict.eta_minutes} min)` : ''} — sync will resume when it finishes.`
    : undefined;

  // `Btn` is a plain render helper, NOT a component defined in the body: a
  // component declared inside a render is a new type on every render, so React
  // unmounts and remounts the real <button> each time — losing focus mid-click
  // and flickering while an action is in flight, which is exactly when these
  // buttons are being watched.
  const btn = ({ key, kind, on, label, working, ns, title }) => {
    const blocked = ns && nsBlocked;
    return (
      <button
        key={key}
        type="button"
        className={`btn btn-sm btn-${kind}`}
        onClick={on}
        disabled={Boolean(busy) || blocked}
        title={(blocked && nsTitle) || title}
        style={(busy || blocked) ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
      >
        {busy === working ? `${working}…` : label}
      </button>
    );
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      padding: '12px 16px', marginBottom: 16, borderRadius: 6,
      background: '#fff', boxShadow: '0 1px 4px rgba(0,33,89,0.08)',
    }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Actions</span>

      {show.approve && btn({
        key: 'approve', kind: 'success', on: approve,
        label: 'Approve', working: 'Approving',
      })}
      {show.push && btn({
        key: 'push', kind: 'primary', on: push, ns: true,
        label: 'Sync to NS', working: 'Syncing to NetSuite',
      })}
      {show.repush && btn({
        key: 'repush', kind: 'primary', on: repush, ns: true,
        label: 'Re-sync', working: 'Re-syncing in NetSuite',
        title: 'Void the existing TimeBills and push the current line items.',
      })}
      {show.retry && btn({
        key: 'retry', kind: 'primary', on: retry, ns: true,
        label: 'Retry', working: 'Retrying sync',
        title: 'Re-push this failed record to NetSuite.',
      })}
      {show.void && btn({
        key: 'void', kind: 'danger', on: voidIt, ns: true,
        label: 'Void', working: 'Voiding in NetSuite',
        title: 'Delete every NetSuite TimeBill for this punch and return it to Pending.',
      })}
      {show.markHandled && btn({
        key: 'markHandled', kind: 'secondary', on: markHandled,
        label: 'Mark Handled', working: 'Marking handled',
        title: 'Billed outside this app. Terminal — no reprocess will move it again.',
      })}
      {show.markExcluded && btn({
        key: 'markExcluded', kind: 'secondary', on: markExcluded,
        label: 'Mark Excluded', working: 'Excluding',
        title: "Not billable, for a reason the engine can't know. Terminal.",
      })}
      {show.unhandle && btn({
        key: 'unhandle', kind: 'secondary', on: unhandle,
        label: 'Undo Handling', working: 'Undoing',
      })}
      {show.unexclude && btn({
        key: 'unexclude', kind: 'secondary', on: unexclude,
        label: 'Undo Exclusion', working: 'Undoing exclusion',
      })}

      {!anyAction && (
        <span style={{ fontSize: 12, color: '#888' }}>
          {canApprove || canPush
            ? `Nothing to do from here while this record is ${record.status.replace(/_/g, ' ')}.`
            : 'Your role can view this record but not act on it.'}
        </span>
      )}

      {msg && (
        <span style={{
          fontSize: 12, marginLeft: 4,
          color: msg.type === 'error' ? '#c00' : '#1c5d54',
        }}>
          {msg.text}
        </span>
      )}
    </div>
  );
}
